import React, { useState, useRef, useEffect } from 'react';
import { Edit2, Save, X, Plus, Trash2 } from 'lucide-react';
import { useEmployeeProfileStore } from '../../../stores/employeeProfileStore';
import { experienceService } from '../../../services/experienceService';
import { DatePicker, Button } from '@t-one-internal/design-system';
import { DateTimeDisplay } from '@t-one-internal/design-system';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@t-one-internal/design-system/lib/components/ui/table';
import { Input } from '@t-one-internal/design-system/lib/components/ui/input';
import type { EmployeeDetails } from '../../../stores/employeeProfileStore';

type ExperienceFieldKey = 'organisation' | 'startDate' | 'jobTitle' | 'endDate' | 'description';

interface ExperienceProfileSectionProps {
  employee: EmployeeDetails;
  onUpdate?: (updates: Partial<EmployeeDetails>) => void;
  readOnly?: boolean;
  isEditing?: boolean;
  disableEdit?: boolean;
  onEditingChange?: (editing: boolean) => void;
}

const ExperienceProfileSection: React.FC<ExperienceProfileSectionProps> = ({
  employee: _employee,
  onUpdate: _onUpdate,
  readOnly = false,
  isEditing: externalIsEditing,
  disableEdit = false,
  onEditingChange,
}) => {
  const [internalIsEditing, setInternalIsEditing] = useState(false);
  const isEditing = externalIsEditing !== undefined ? externalIsEditing : internalIsEditing;
  const setIsEditing = setInternalIsEditing;
  const [isSaving, setIsSaving] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Record<string, Partial<{ organisation: string, startDate: string, jobTitle: string, endDate: string, description: string }>>>({});
  const [pendingDeletions, setPendingDeletions] = useState<string[]>([]);
  const { selectedEmployee, addExperience, removeExperience, updateExperience } = useEmployeeProfileStore();
  const [validationError, setValidationError] = useState<string | null>(null);
  const [dateErrors, setDateErrors] = useState<Record<string, string>>({});

  const experienceEntries = selectedEmployee?.experiences ?? [];
  const visibleExperienceEntries = experienceEntries.filter(entry => !pendingDeletions.includes(entry.id));

  // Calculate total years of experience from all visible entries' start/end dates
  const calculateTotalFromEntries = (): number => {
    let totalDays = 0;
    for (const entry of experienceEntries) {
      if (pendingDeletions.includes(entry.id)) continue;
      const pending = pendingChanges[entry.id] || {};
      const startISO = pending.startDate ?? entry.startDateISO ?? entry.startDate;
      const endISO = pending.endDate ?? entry.endDateISO ?? entry.endDate;
      if (!startISO || startISO === 'null' || startISO === '') continue;
      const startDate = new Date(startISO);
      if (isNaN(startDate.getTime())) continue;
      const endDate = (endISO && endISO !== 'null' && endISO !== '') ? new Date(endISO) : new Date();
      if (isNaN(endDate.getTime())) continue;
      const days = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
      if (days > 0) totalDays += days;
    }
    return Math.round((totalDays / 365.25) * 10) / 10;
  };

  const calculatedTotalExperience = calculateTotalFromEntries();

  // Helper: format a date string (YYYY-MM-DD or ISO) to display format
  const formatDateForDisplay = (dateStr: string | null | undefined): string => {
    if (!dateStr || dateStr === 'null' || dateStr === '') return '-';
    try {
      return DateTimeDisplay.toStandardDate(dateStr);
    } catch {
      return dateStr;
    }
  };

  // Validate start/end date pair for a given entry
  const validateDates = (entryId: string, changedField: 'startDate' | 'endDate', startDate: string | null | undefined, endDate: string | null | undefined) => {
    const startKey = `${entryId}_startDate`;
    const endKey = `${entryId}_endDate`;
    if (startDate && endDate && startDate !== '' && endDate !== '') {
      if (endDate < startDate) {
        const errorMsg = changedField === 'startDate'
          ? 'Start date cannot be after end date'
          : 'End date cannot be before start date';
        const errorKey = changedField === 'startDate' ? startKey : endKey;
        // Set error on the changed field, clear the other
        const otherKey = changedField === 'startDate' ? endKey : startKey;
        setDateErrors(prev => {
          const next = { ...prev, [errorKey]: errorMsg };
          delete next[otherKey];
          return next;
        });
        return false;
      }
    }
    // Valid — clear both keys for this entry
    setDateErrors(prev => {
      const next = { ...prev };
      delete next[startKey];
      delete next[endKey];
      return next;
    });
    return true;
  };

  const hasDateErrors = Object.keys(dateErrors).length > 0;

  const handleEdit = () => {
    setIsEditing(true);
    onEditingChange?.(true);
  };

  const handleSave = async (skipEditingToggle = false) => {
    if (isSaving) return;
    setIsSaving(true);
    const userIdentifier = selectedEmployee?.user_uuid || selectedEmployee?.id;
    if (!userIdentifier) {
      console.error('No user identifier available');
      setIsEditing(false);
      setIsSaving(false);
      return;
    }

    // Total experience is auto-calculated from entries' dates
    const totalExperienceToSave = calculatedTotalExperience;

    // Block save if date validation errors exist
    if (hasDateErrors) {
      setIsSaving(false);
      return;
    }

    for (const entry of experienceEntries) {
      if (pendingDeletions.includes(entry.id)) continue;
      const pending = pendingChanges[entry.id] || {};
      const org = pending.organisation !== undefined ? pending.organisation : entry.organisation;
      const title = pending.jobTitle !== undefined ? pending.jobTitle : entry.jobTitle;
      if (!org?.trim() || !title?.trim()) {
        setValidationError('Organization and Job Title are required for all experience entries');
        setIsSaving(false);
        return;
      }
    }

    setValidationError(null);

    // Process deletions
    for (const experienceId of pendingDeletions) {
      try {
        const numericId = parseInt(experienceId);
        const isNewRecord = isNaN(numericId) || numericId > 1000000000000;

        if (!isNewRecord && numericId > 0) {
          await experienceService.deleteExperience(userIdentifier, numericId);
        }
        await removeExperience(experienceId);
      } catch (error) {
        console.error(`Failed to delete experience ${experienceId}:`, error);
      }
    }

    // Process changes
    let madeApiUpdate = false;
    for (const [experienceId, changes] of Object.entries(pendingChanges)) {
      if (pendingDeletions.includes(experienceId)) continue;
      if (Object.keys(changes).length === 0) continue;

      try {
        const experienceEntry = selectedEmployee?.experiences?.find(exp => exp.id === experienceId);
        if (!experienceEntry) continue;

        const formatDateForAPI = (date: any): string => {
          if (!date) return '';
          if (date instanceof Date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          }
          if (typeof date === 'string') {
            if (date.match(/^\d{4}-\d{2}-\d{2}$/)) return date;
            if (date.includes('T')) return date.split('T')[0];
            const parsed = new Date(date);
            if (!isNaN(parsed.getTime())) {
              const year = parsed.getFullYear();
              const month = String(parsed.getMonth() + 1).padStart(2, '0');
              const day = String(parsed.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            }
          }
          return '';
        };

        const updateData: any = {
          company: changes.organisation ?? experienceEntry.organisation ?? '',
          position: changes.jobTitle ?? experienceEntry.jobTitle ?? '',
          total_years_experience: totalExperienceToSave,
          description: changes.description ?? experienceEntry.description ?? ''
        };

        const startDate = changes.startDate ?? experienceEntry.startDateISO ?? experienceEntry.startDate;
        if (startDate && startDate !== 'null' && startDate !== '') {
          updateData.start_date = formatDateForAPI(startDate);
        }

        const endDate = changes.endDate ?? experienceEntry.endDateISO ?? experienceEntry.endDate;
        if (endDate && endDate !== 'null' && endDate !== '') {
          updateData.end_date = formatDateForAPI(endDate);
        }

        const numericId = parseInt(experienceId);
        const isNewRecord = isNaN(numericId) || numericId > 1000000000000;

        if (!isNewRecord && numericId > 0) {
          await experienceService.updateExperience(userIdentifier, numericId, updateData);
          madeApiUpdate = true;
        } else {
          const createResponse = await experienceService.createExperience(userIdentifier, updateData);
          const newExperience = createResponse.experience;
          if (newExperience && newExperience.id) {
            const finalExperienceId = String(newExperience.id);
            await removeExperience(experienceId);
            const storeState = useEmployeeProfileStore.getState();
            const currentEmployee = storeState.selectedEmployee;
            if (currentEmployee) {
              const newStartISO = formatDateForAPI(changes.startDate ?? experienceEntry.startDateISO ?? experienceEntry.startDate) || null;
              const newEndISO = formatDateForAPI(changes.endDate ?? experienceEntry.endDateISO ?? experienceEntry.endDate) || null;
              const newExperienceEntry = {
                id: finalExperienceId,
                organisation: changes.organisation || experienceEntry.organisation || null,
                jobTitle: changes.jobTitle || experienceEntry.jobTitle || null,
                startDate: formatDateForDisplay(newStartISO),
                endDate: formatDateForDisplay(newEndISO),
                startDateISO: newStartISO,
                endDateISO: newEndISO,
                description: changes.description || experienceEntry.description || null,
              };
              const updatedExperiences = [...(currentEmployee.experiences || []), newExperienceEntry];
              useEmployeeProfileStore.setState({
                selectedEmployee: {
                  ...currentEmployee,
                  experiences: updatedExperiences,
                  lastUpdated: new Date()
                }
              });
            }
            madeApiUpdate = true;
          }
        }

        if (!isNewRecord) {
          const newStartDate = changes.startDate ?? experienceEntry.startDateISO ?? experienceEntry.startDate;
          const newEndDate = changes.endDate ?? experienceEntry.endDateISO ?? experienceEntry.endDate;
          await updateExperience(experienceId, {
            organisation: changes.organisation ?? experienceEntry.organisation ?? null,
            jobTitle: changes.jobTitle ?? experienceEntry.jobTitle ?? null,
            startDateISO: newStartDate || null,
            startDate: formatDateForDisplay(newStartDate),
            endDateISO: newEndDate || null,
            endDate: formatDateForDisplay(newEndDate),
            description: changes.description ?? experienceEntry.description ?? null,
          });
        }
      } catch (error) {
        console.error(`Failed to save experience ${experienceId}:`, error);
      }
    }

    // Update total in store (backend recalculates on its side too)
    const storeState = useEmployeeProfileStore.getState();
    if (storeState.selectedEmployee) {
      useEmployeeProfileStore.setState({
        selectedEmployee: {
          ...storeState.selectedEmployee,
          totalYearsOfExperience: totalExperienceToSave || null,
          lastUpdated: new Date()
        }
      });
    }

    setPendingChanges({});
    setPendingDeletions([]);
    setDateErrors({});
    if (!skipEditingToggle) { setIsEditing(false); onEditingChange?.(false); }
    setIsSaving(false);
  };

  const prevExternalIsEditing = useRef(externalIsEditing);
  useEffect(() => {
    if (!externalIsEditing && prevExternalIsEditing.current) {
      handleSave(true);
    }
    prevExternalIsEditing.current = externalIsEditing;
  }, [externalIsEditing]);

  const handleCancel = () => {
    setPendingChanges({});
    setPendingDeletions([]);
    setDateErrors({});
    setValidationError(null);
    setIsEditing(false);
    onEditingChange?.(false);
  };

  const handleAddExperience = async () => {
    await addExperience({
      organisation: null,
      startDate: null,
      jobTitle: null,
      endDate: null,
      description: null,
    });
  };

  const handleRemoveExperience = (id: string) => {
    setPendingDeletions(prev => [...prev, id]);
  };

  const handleUpdateExperience = (id: string, field: ExperienceFieldKey, value: string) => {
    setPendingChanges(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value
      }
    }));
  };

  const handleDateChange = (id: string, field: 'startDate' | 'endDate', date: Date | undefined) => {
    if (!date) {
      handleUpdateExperience(id, field, '');
      // Re-validate: clearing a date clears the error
      const entry = experienceEntries.find(e => e.id === id);
      const pending = pendingChanges[id] || {};
      if (field === 'startDate') {
        validateDates(id, field, '', pending.endDate ?? entry?.endDateISO ?? entry?.endDate ?? '');
      } else {
        validateDates(id, field, pending.startDate ?? entry?.startDateISO ?? entry?.startDate ?? '', '');
      }
      return;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateString = `${year}-${month}-${day}`;
    handleUpdateExperience(id, field, dateString);

    // Validate against the other date
    const entry = experienceEntries.find(e => e.id === id);
    const pending = pendingChanges[id] || {};
    if (field === 'startDate') {
      const otherDate = pending.endDate ?? entry?.endDateISO ?? entry?.endDate ?? '';
      validateDates(id, field, dateString, otherDate);
    } else {
      const otherDate = pending.startDate ?? entry?.startDateISO ?? entry?.startDate ?? '';
      validateDates(id, field, otherDate, dateString);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-900">Experience</h3>
        <div className="flex items-center gap-2">
          {isEditing && (
            <Button variant="outline" size="sm" type="button" onClick={handleAddExperience}>
              <Plus className="w-4 h-4" />
              Add Experience
            </Button>
          )}
          {!readOnly && externalIsEditing === undefined && (
            <>
              {!isEditing ? (
                <Button variant="ghost" size="sm" type="button" onClick={handleEdit} disabled={disableEdit} className={disableEdit ? 'opacity-40 cursor-not-allowed' : ''}>
                  <Edit2 className="w-4 h-4" />
                  Edit
                </Button>
              ) : (
                <>
                  <Button size="sm" type="button" onClick={() => handleSave()} disabled={isSaving || hasDateErrors} className={(isSaving || hasDateErrors) ? 'opacity-50 cursor-not-allowed' : ''}>
                    <Save className="w-4 h-4" />
                    {isSaving ? 'Saving...' : 'Save'}
                  </Button>
                  <Button variant="ghost" size="sm" type="button" onClick={handleCancel}>
                    <X className="w-4 h-4" />
                    Cancel
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {validationError && (
        <div className="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-md border border-red-200">
          {validationError}
        </div>
      )}

      {/* Total Years of Experience (auto-calculated from entries) */}
      <div className="flex items-center gap-4">
        <label className="text-sm font-medium text-slate-500 whitespace-nowrap">Total Years of Experience</label>
        <div className="text-sm font-medium text-slate-900">
          {calculatedTotalExperience > 0 ? calculatedTotalExperience : (selectedEmployee?.totalYearsOfExperience ?? '-')}
        </div>
      </div>

      {/* Experience Entries Table */}
      {visibleExperienceEntries.length === 0 ? (
        <div className="text-sm text-slate-500 italic py-4">No experience records found.</div>
      ) : (
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organization</TableHead>
                <TableHead>Job Title</TableHead>
                <TableHead>Start Date</TableHead>
                <TableHead>End Date</TableHead>
                <TableHead>Description</TableHead>
                {isEditing && <TableHead className="w-[60px]" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleExperienceEntries.map((entry) => {
                const pending = pendingChanges[entry.id] || {};
                return (
                  <TableRow key={entry.id}>
                    <TableCell>
                      {isEditing ? (
                        <Input
                          value={pending.organisation ?? entry.organisation ?? ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleUpdateExperience(entry.id, 'organisation', e.target.value)}
                          placeholder="Enter organisation"
                        />
                      ) : (
                        <span className="text-sm text-slate-900">{entry.organisation || '-'}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <Input
                          value={pending.jobTitle ?? entry.jobTitle ?? ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleUpdateExperience(entry.id, 'jobTitle', e.target.value)}
                          placeholder="Enter job title"
                        />
                      ) : (
                        <span className="text-sm text-slate-900">{entry.jobTitle || '-'}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <div>
                          <DatePicker
                            value={pending.startDate ?? entry.startDateISO ?? (typeof entry.startDate === 'string' ? entry.startDate : undefined) ?? undefined}
                            onChange={(date: string | undefined) => handleDateChange(entry.id, 'startDate', date ? new Date(date) : undefined)}
                            placeholder="Start date"
                            className="h-9 text-sm"
                          />
                          {dateErrors[`${entry.id}_startDate`] && (
                            <p className="text-xs text-red-500 mt-1">{dateErrors[`${entry.id}_startDate`]}</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-slate-900">
                          {pending.startDate ? formatDateForDisplay(pending.startDate) : (entry.startDate ?? '-')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <div>
                          <DatePicker
                            value={pending.endDate ?? entry.endDateISO ?? (typeof entry.endDate === 'string' ? entry.endDate : undefined) ?? undefined}
                            onChange={(date: string | undefined) => handleDateChange(entry.id, 'endDate', date ? new Date(date) : undefined)}
                            placeholder="End date"
                            className="h-9 text-sm"
                          />
                          {dateErrors[`${entry.id}_endDate`] && (
                            <p className="text-xs text-red-500 mt-1">{dateErrors[`${entry.id}_endDate`]}</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-slate-900">
                          {pending.endDate ? formatDateForDisplay(pending.endDate) : (entry.endDate ?? '-')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditing ? (
                        <textarea
                          value={pending.description ?? entry.description ?? ''}
                          onChange={(e) => handleUpdateExperience(entry.id, 'description', e.target.value)}
                          className="w-full px-3 py-2 text-sm text-slate-900 bg-white border border-input rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 resize-none"
                          placeholder="Description"
                          rows={2}
                        />
                      ) : (
                        <span className="text-sm text-slate-900 line-clamp-2">{entry.description || '-'}</span>
                      )}
                    </TableCell>
                    {isEditing && (
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => handleRemoveExperience(entry.id)}
                          className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
                          title="Delete Experience"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default ExperienceProfileSection;
